// All UI strings for VATcalc.eu in EN/DE/FR/ES/IT.
// Country names are kept in English across locales (industry standard for EU B2B VAT tools).

const translations = {
    en: {
        meta: { localeLabel: "English", flag: "🇬🇧" },
        nav: {
            calculator: "Calculator",
            vatRates: "VAT Rates",
            howItWorks: "How It Works",
            faq: "FAQ",
            share: "Share this tool",
            openCalculator: "Open Calculator",
            language: "Language",
        },
        banner: {
            needHelp: "Need help filing VAT?",
            ctaText: "Try Quaderno — automated OSS filing, invoicing & VAT compliance.",
            cta: "Try Quaderno →",
            dismiss: "Dismiss",
        },
        hero: {
            eyebrow: "EU VAT Calculator · 2026 rates",
            titleLine1: "Calculate ",
            titleHighlight: "EU VAT",
            titleLine2: " in seconds.",
            subtitle:
                "A free, instant cross-border VAT tool for e-commerce sellers, freelancers and finance teams. Pick a seller country, a buyer country and a product category — we handle the rest.",
        },
        calc: {
            transactionDetails: "Transaction details",
            summary: "Summary",
            netPrice: "Net price (€)",
            sellerCountry: "Seller country",
            buyerCountry: "Buyer country",
            productCategory: "Product category",
            vatRateApplied: "VAT rate applied",
            vatAmount: "VAT amount",
            netPriceLabel: "Net price",
            grossPrice: "Gross price (customer pays)",
            vatIncluded: "VAT included",
            copyLink: "Copy shareable link",
            copyHint: "Copies a link with these inputs pre-filled",
            copied: "Link copied — share away!",
            ossNotice: "You can use the EU OSS scheme — register once in {seller} and declare VAT for all EU sales.",
            thresholdNotice:
                "If your annual cross-border B2C sales stay under €10,000, you can use your home country VAT rate ({sellerFlag} {seller}) instead.",
            domesticNotice:
                "Domestic sale within {sellerFlag} {seller} — local VAT rules apply, OSS is not required.",
        },
        rateLabels: {
            standard: "Standard",
            reduced: "Reduced",
            standardNoReduced: "Standard (no reduced)",
        },
        categories: {
            standard: { label: "Standard Rate", description: "Default — most goods & services" },
            reduced: { label: "Reduced Rate", description: "Country-specific reduced rate" },
            digital: { label: "Digital Services", description: "Apps, SaaS, downloads — taxed at standard" },
            food: { label: "Food", description: "Groceries — usually reduced rate" },
            books: { label: "Books & Publications", description: "Print + e-books — typically reduced" },
            medicine: { label: "Medicine", description: "Pharmaceuticals — usually reduced" },
        },
        oss: {
            eyebrow: "Tool 02 · Threshold tracker",
            titleLine1: "Are you below the ",
            titleHighlight: "€10,000",
            titleLine2: " threshold?",
            intro:
                "Sellers under €10,000 in cross-border B2C sales per year can charge their home country VAT rate. Above it, OSS registration is required.",
            cardTitle: "OSS Threshold Tracker",
            homeCountry: "Home country",
            sales: "Cross-border B2C sales (this year)",
            thresholdUsed: "Threshold used",
            ofCap: "of annual cap",
            below: "Below threshold — use home VAT rate",
            above: "Above threshold — OSS registration required",
            belowDesc:
                "You can keep applying {flag} {country} VAT to all EU customers until you cross €10,000 in cross-border B2C sales this calendar year.",
            aboveDesc:
                "You've exceeded €10,000. Register for OSS in {country} and start charging the buyer's country VAT rate on every cross-border B2C sale.",
        },
        estimator: {
            eyebrow: "Tool 03 · Quarterly estimator",
            titleLine1: "What do I owe",
            titleLine2: " this quarter?",
            intro:
                "Add up to 10 sales rows by destination country. We apply each country's standard VAT rate and total your OSS-filing obligation.",
            salesRows: "Sales rows",
            addRow: "Add row",
            max: "max",
            country: "Country",
            sales: "Sales (€)",
            vatRate: "VAT rate",
            vatOwed: "VAT owed",
            totalSales: "Total sales",
            totalVat: "Total VAT owed",
            fileViaOss: "File via OSS",
            ossSubtitle: "One quarterly return for all EU sales",
            removeRow: "Remove row",
            exportCsv: "Export CSV",
            exportPdf: "Export PDF",
            csvExported: "CSV downloaded",
            pdfExported: "PDF downloaded",
            pdfError: "Could not generate PDF",
            pdfTitle: "Quarterly VAT Estimate",
            pdfGenerated: "Generated",
        },
        how: {
            eyebrow: "Primer",
            title: "How EU VAT works for online sellers",
            intro:
                "Three concepts cover ~90% of cross-border VAT decisions for digital and e-commerce sellers in 2026.",
            items: [
                {
                    title: "What is VAT?",
                    body: "Value Added Tax is a consumption tax charged on goods and services in the EU. The seller collects it from the buyer and forwards it to the tax authority of the buyer's country.",
                },
                {
                    title: "The €10,000 threshold rule",
                    body: "If your annual cross-border B2C sales across the EU stay below €10,000, you can keep charging your home country VAT. Above it, you must apply the buyer's country rate.",
                },
                {
                    title: "What is OSS?",
                    body: "The One-Stop Shop scheme lets you register once in your home country and file a single quarterly VAT return for all your cross-border EU sales — no more 27 separate registrations.",
                },
            ],
        },
        rates: {
            eyebrow: "Reference",
            title: "VAT rates across the 27 EU countries",
            search: "Search a country…",
            country: "Country",
            standard: "Standard",
            reduced1: "Reduced 1",
            reduced2: "Reduced 2",
            noMatch: 'No country matches "{q}".',
            lastUpdated: "Source: European Commission. VAT rates last updated: January 2026.",
        },
        faq: {
            eyebrow: "Frequently asked",
            title: "Questions sellers ask us",
            intro: "Plain-English answers to the most common cross-border VAT scenarios.",
            items: [
                {
                    q: "Do I need to charge VAT if I sell from Latvia to Germany?",
                    a: "Yes, if you sell B2C and have crossed the €10,000 cross-border threshold, you must charge German VAT (19% standard) and remit it via OSS. Below €10,000 you may keep applying Latvia's domestic rate.",
                },
                {
                    q: "What is the EU OSS scheme?",
                    a: "The One-Stop Shop is a single online portal where EU-based sellers can declare and pay VAT for all their cross-border B2C sales across the EU. You register once in your home country and file a single quarterly return.",
                },
                {
                    q: "What happens if I go over the €10,000 threshold?",
                    a: "From the moment you cross €10,000 in cross-border B2C sales in a calendar year, you must charge each customer the VAT rate of their country and report it through OSS. The home-country rate option no longer applies.",
                },
                {
                    q: "Are digital products taxed differently?",
                    a: "Since 2015, digital services (apps, SaaS, e-books, streaming) are always taxed in the buyer's country at that country's standard VAT rate, regardless of the €10,000 threshold for goods.",
                },
                {
                    q: "Do I charge VAT on B2B sales?",
                    a: "Generally no — B2B intra-EU sales fall under the reverse-charge mechanism if the buyer provides a valid VAT number. The buyer self-accounts for VAT in their country, and you issue an invoice without VAT.",
                },
                {
                    q: "What is IOSS for imports under €150?",
                    a: "The Import One-Stop Shop allows non-EU sellers (or marketplaces) to collect VAT at checkout on goods imported into the EU with a value of €150 or less, simplifying customs clearance and avoiding surprise fees for buyers.",
                },
            ],
        },
        affiliateCard: {
            recommended: "Recommended",
            title: "Automate your VAT compliance",
            intro:
                "Stop tracking thresholds in spreadsheets. Tools like Taxually, Quaderno and Avalara handle OSS filing, invoicing, and country-specific VAT rules automatically.",
            getStarted: "Get started",
            partners: {
                taxually: { name: "Taxually", desc: "OSS & cross-border VAT filing across the EU." },
                quaderno: { name: "Quaderno", desc: "Tax compliance + invoicing for SaaS & e-commerce." },
                avalara: { name: "Avalara", desc: "Enterprise-grade VAT automation & reporting." },
            },
        },
        newsletter: {
            eyebrow: "Stay updated",
            titleLine1: "Subscribe for ",
            titleHighlight: "EU VAT update alerts",
            intro:
                "Get an email whenever a member state changes its VAT rates or OSS rules. No spam, unsubscribe anytime.",
            name: "Your name",
            email: "you@company.eu",
            submit: "Subscribe",
            subscribing: "Subscribing…",
            subscribed: "Subscribed ✓",
            disclaimer:
                "By subscribing you agree to receive occasional product updates. We never share your email.",
            errorMissing: "Please enter your name and email",
            errorGeneric: "Something went wrong. Please try again.",
            success: "Subscribed — we'll email you when EU VAT rates change.",
        },
        footer: {
            resources: "Resources",
            company: "Company",
            about: "About",
            privacy: "Privacy Policy",
            contact: "Contact",
            tagline:
                "Free EU VAT calculator with OSS threshold tracker and quarterly estimator. Built for cross-border sellers in 2026.",
            lastUpdated: "VAT rates last updated:",
            updateMonth: "January 2026",
            source: "Source: European Commission",
            disclaimer:
                "This tool is for informational purposes only. Consult a tax professional for official advice.",
        },
        vies: {
            eyebrow: "Tool 04 · VAT number lookup",
            titleLine1: "Validate any ",
            titleHighlight: "EU VAT number",
            titleLine2: " against VIES",
            intro:
                "Check if a buyer's VAT registration is real and active in the official European Commission VIES database. Essential for B2B reverse-charge invoicing.",
            cardTitle: "VIES VAT number validator",
            country: "Country",
            vatNumber: "VAT number",
            validate: "Validate",
            validating: "Checking…",
            valid: "Valid VAT number",
            invalid: "Not a valid VAT number",
            errorEmpty: "Enter a VAT number to validate.",
            errorService: "Could not reach VIES. Please try again in a moment.",
            reverseChargeTitle: "Reverse-charge applies",
            reverseChargeBody:
                "For cross-border B2B sales to a {country} VAT-registered business, you charge 0% VAT on your invoice. The buyer self-accounts for VAT in their country.",
        },
        popular: {
            eyebrow: "Popular routes",
            title: "Cross-border VAT, pre-filled for you",
            intro: "Common EU sales lanes. Click a route to load it instantly into the calculator above.",
            standard: "standard rate",
            allCountries: "All countries (from Germany)",
        },
        share: {
            copied: "Link copied to clipboard",
            title: "EU VAT Calculator 2026 — VATcalc.eu",
            text: "Free EU VAT calculator with OSS threshold tracker and quarterly estimator.",
        },
    },

    de: {
        meta: { localeLabel: "Deutsch", flag: "🇩🇪" },
        nav: {
            calculator: "Rechner",
            vatRates: "MwSt.-Sätze",
            howItWorks: "So funktioniert's",
            faq: "FAQ",
            share: "Tool teilen",
            openCalculator: "Rechner öffnen",
            language: "Sprache",
        },
        banner: {
            needHelp: "Hilfe bei der MwSt.-Erklärung?",
            ctaText: "Probiere Quaderno — automatisierte OSS-Erklärung, Rechnungen & MwSt.-Compliance.",
            cta: "Quaderno testen →",
            dismiss: "Schließen",
        },
        hero: {
            eyebrow: "EU-MwSt.-Rechner · Sätze 2026",
            titleLine1: "Berechne ",
            titleHighlight: "EU-MwSt.",
            titleLine2: " in Sekunden.",
            subtitle:
                "Kostenloses, sofortiges grenzüberschreitendes MwSt.-Tool für E-Commerce-Verkäufer, Freiberufler und Finanzteams. Wähle Verkäufer- und Käuferland und eine Produktkategorie — wir erledigen den Rest.",
        },
        calc: {
            transactionDetails: "Transaktionsdetails",
            summary: "Zusammenfassung",
            netPrice: "Nettopreis (€)",
            sellerCountry: "Verkäuferland",
            buyerCountry: "Käuferland",
            productCategory: "Produktkategorie",
            vatRateApplied: "Angewandter MwSt.-Satz",
            vatAmount: "MwSt.-Betrag",
            netPriceLabel: "Nettopreis",
            grossPrice: "Bruttopreis (Kunde zahlt)",
            vatIncluded: "MwSt. inkl.",
            copyLink: "Teilbaren Link kopieren",
            copyHint: "Kopiert einen Link mit diesen Eingaben",
            copied: "Link kopiert — viel Spaß beim Teilen!",
            ossNotice:
                "Du kannst das EU-OSS-Verfahren nutzen — registriere dich einmal in {seller} und erkläre die MwSt. für alle EU-Verkäufe.",
            thresholdNotice:
                "Bleiben deine grenzüberschreitenden B2C-Jahresumsätze unter 10.000 €, kannst du stattdessen den MwSt.-Satz deines Heimatlandes ({sellerFlag} {seller}) verwenden.",
            domesticNotice:
                "Inlandsverkauf in {sellerFlag} {seller} — lokale MwSt.-Regeln gelten, OSS ist nicht nötig.",
        },
        rateLabels: {
            standard: "Regelsatz",
            reduced: "Ermäßigt",
            standardNoReduced: "Regelsatz (kein ermäßigter)",
        },
        categories: {
            standard: { label: "Regelsatz", description: "Standard — die meisten Güter & Dienste" },
            reduced: { label: "Ermäßigter Satz", description: "Länderspezifischer ermäßigter Satz" },
            digital: { label: "Digitale Dienste", description: "Apps, SaaS, Downloads — Regelsatz" },
            food: { label: "Lebensmittel", description: "Lebensmittel — meist ermäßigt" },
            books: { label: "Bücher & Publikationen", description: "Print + E-Books — meist ermäßigt" },
            medicine: { label: "Arzneimittel", description: "Medikamente — meist ermäßigt" },
        },
        oss: {
            eyebrow: "Tool 02 · Schwellenwert-Tracker",
            titleLine1: "Bist du unter der ",
            titleHighlight: "10.000-€-",
            titleLine2: "Schwelle?",
            intro:
                "Verkäufer unter 10.000 € grenzüberschreitendem B2C-Umsatz pro Jahr können den heimischen MwSt.-Satz nutzen. Darüber ist die OSS-Registrierung erforderlich.",
            cardTitle: "OSS-Schwellenwert-Tracker",
            homeCountry: "Heimatland",
            sales: "Grenzüberschreitende B2C-Umsätze (dieses Jahr)",
            thresholdUsed: "Schwelle genutzt",
            ofCap: "der Jahresgrenze",
            below: "Unter Schwelle — heimischen MwSt.-Satz nutzen",
            above: "Über Schwelle — OSS-Registrierung erforderlich",
            belowDesc:
                "Du kannst {flag} {country}-MwSt. weiterhin auf alle EU-Kunden anwenden, bis du 10.000 € grenzüberschreitenden B2C-Umsatz in diesem Kalenderjahr überschreitest.",
            aboveDesc:
                "Du hast 10.000 € überschritten. Registriere dich für OSS in {country} und berechne den MwSt.-Satz des Käuferlandes für jeden grenzüberschreitenden B2C-Verkauf.",
        },
        estimator: {
            eyebrow: "Tool 03 · Quartalsschätzer",
            titleLine1: "Was schulde ich",
            titleLine2: " dieses Quartal?",
            intro:
                "Füge bis zu 10 Verkaufsreihen nach Zielland hinzu. Wir wenden den Regelsatz jedes Landes an und summieren deine OSS-Pflicht.",
            salesRows: "Verkaufsreihen",
            addRow: "Reihe hinzufügen",
            max: "max",
            country: "Land",
            sales: "Umsatz (€)",
            vatRate: "MwSt.-Satz",
            vatOwed: "MwSt. fällig",
            totalSales: "Gesamtumsatz",
            totalVat: "Gesamte MwSt.-Schuld",
            fileViaOss: "Über OSS einreichen",
            ossSubtitle: "Eine Quartalserklärung für alle EU-Verkäufe",
            removeRow: "Reihe entfernen",
            exportCsv: "CSV exportieren",
            exportPdf: "PDF exportieren",
            csvExported: "CSV heruntergeladen",
            pdfExported: "PDF heruntergeladen",
            pdfError: "PDF konnte nicht erstellt werden",
            pdfTitle: "Quartals-MwSt.-Schätzung",
            pdfGenerated: "Erstellt",
        },
        how: {
            eyebrow: "Grundlagen",
            title: "So funktioniert die EU-MwSt. für Online-Verkäufer",
            intro:
                "Drei Konzepte decken ~90 % aller grenzüberschreitenden MwSt.-Entscheidungen für digitale & E-Commerce-Verkäufer in 2026 ab.",
            items: [
                {
                    title: "Was ist MwSt.?",
                    body: "Die Mehrwertsteuer ist eine Verbrauchssteuer auf Waren und Dienstleistungen in der EU. Der Verkäufer zieht sie vom Käufer ein und führt sie an die Steuerbehörde des Käuferlandes ab.",
                },
                {
                    title: "Die 10.000-€-Schwellenregel",
                    body: "Bleiben deine grenzüberschreitenden B2C-Jahresumsätze unter 10.000 €, kannst du weiterhin deine heimische MwSt. berechnen. Darüber musst du den Satz des Käuferlandes anwenden.",
                },
                {
                    title: "Was ist OSS?",
                    body: "Das One-Stop-Shop-Verfahren erlaubt eine einmalige Registrierung im Heimatland und eine einzige Quartals-MwSt.-Erklärung für alle grenzüberschreitenden EU-Verkäufe — keine 27 Einzelregistrierungen.",
                },
            ],
        },
        rates: {
            eyebrow: "Referenz",
            title: "MwSt.-Sätze in den 27 EU-Ländern",
            search: "Land suchen…",
            country: "Land",
            standard: "Regel",
            reduced1: "Ermäßigt 1",
            reduced2: "Ermäßigt 2",
            noMatch: 'Kein Land passt zu "{q}".',
            lastUpdated:
                "Quelle: Europäische Kommission. MwSt.-Sätze zuletzt aktualisiert: Januar 2026.",
        },
        faq: {
            eyebrow: "Häufig gestellt",
            title: "Fragen, die Verkäufer uns stellen",
            intro: "Klare Antworten auf die häufigsten grenzüberschreitenden MwSt.-Szenarien.",
            items: [
                {
                    q: "Muss ich MwSt. berechnen, wenn ich aus Lettland nach Deutschland verkaufe?",
                    a: "Ja, wenn du B2C verkaufst und die 10.000-€-Schwelle überschritten hast, musst du deutsche MwSt. (19 % Regelsatz) berechnen und über OSS abführen. Unter 10.000 € darfst du weiterhin den lettischen Heimatsatz anwenden.",
                },
                {
                    q: "Was ist das EU-OSS-Verfahren?",
                    a: "Der One-Stop-Shop ist ein Online-Portal, in dem EU-Verkäufer die MwSt. für alle grenzüberschreitenden B2C-Verkäufe in der EU erklären und zahlen. Eine Registrierung im Heimatland — eine Quartalserklärung.",
                },
                {
                    q: "Was passiert, wenn ich die 10.000-€-Schwelle überschreite?",
                    a: "Ab dem Moment des Überschreitens im Kalenderjahr musst du jedem Kunden den MwSt.-Satz seines Landes berechnen und über OSS melden. Der Heimatsatz ist nicht mehr anwendbar.",
                },
                {
                    q: "Werden digitale Produkte anders besteuert?",
                    a: "Seit 2015 werden digitale Dienste (Apps, SaaS, E-Books, Streaming) immer im Käuferland zum dortigen Regelsatz besteuert — unabhängig von der 10.000-€-Schwelle für Waren.",
                },
                {
                    q: "Berechne ich MwSt. bei B2B-Verkäufen?",
                    a: "In der Regel nicht — innergemeinschaftliche B2B-Verkäufe unterliegen dem Reverse-Charge-Verfahren, sofern der Käufer eine gültige USt-IdNr. hat. Der Käufer rechnet die MwSt. selbst ab; deine Rechnung erfolgt ohne MwSt.",
                },
                {
                    q: "Was ist IOSS für Importe unter 150 €?",
                    a: "Der Import One-Stop-Shop ermöglicht Nicht-EU-Verkäufern (oder Marktplätzen), die MwSt. an der Kasse für Waren bis 150 € einzuziehen — vereinfacht den Zoll und vermeidet Überraschungsgebühren für Käufer.",
                },
            ],
        },
        affiliateCard: {
            recommended: "Empfohlen",
            title: "Automatisiere deine MwSt.-Compliance",
            intro:
                "Schluss mit Schwellen-Tracking in Tabellen. Tools wie Taxually, Quaderno und Avalara erledigen OSS-Erklärung, Rechnungen und länderspezifische MwSt.-Regeln automatisch.",
            getStarted: "Loslegen",
            partners: {
                taxually: { name: "Taxually", desc: "OSS- & grenzüberschreitende MwSt.-Erklärungen EU-weit." },
                quaderno: { name: "Quaderno", desc: "Steuer-Compliance + Rechnungen für SaaS & E-Commerce." },
                avalara: { name: "Avalara", desc: "MwSt.-Automatisierung & Reporting auf Enterprise-Niveau." },
            },
        },
        newsletter: {
            eyebrow: "Bleib informiert",
            titleLine1: "Abonniere ",
            titleHighlight: "EU-MwSt.-Updates",
            intro:
                "Erhalte eine E-Mail, sobald ein Mitgliedstaat seine MwSt.-Sätze oder OSS-Regeln ändert. Kein Spam, jederzeit abbestellbar.",
            name: "Dein Name",
            email: "du@firma.eu",
            submit: "Abonnieren",
            subscribing: "Wird abonniert…",
            subscribed: "Abonniert ✓",
            disclaimer:
                "Mit dem Abo erhältst du gelegentliche Produkt-Updates. Wir geben deine E-Mail nicht weiter.",
            errorMissing: "Bitte Name und E-Mail angeben",
            errorGeneric: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
            success: "Abonniert — wir melden uns bei MwSt.-Änderungen.",
        },
        footer: {
            resources: "Ressourcen",
            company: "Unternehmen",
            about: "Über uns",
            privacy: "Datenschutz",
            contact: "Kontakt",
            tagline:
                "Kostenloser EU-MwSt.-Rechner mit OSS-Schwellen-Tracker und Quartalsschätzer. Für grenzüberschreitende Verkäufer in 2026.",
            lastUpdated: "MwSt.-Sätze zuletzt aktualisiert:",
            updateMonth: "Januar 2026",
            source: "Quelle: Europäische Kommission",
            disclaimer:
                "Dieses Tool dient nur zu Informationszwecken. Konsultiere einen Steuerberater für offizielle Beratung.",
        },
        vies: {
            eyebrow: "Tool 04 · USt-IdNr.-Prüfung",
            titleLine1: "Prüfe jede ",
            titleHighlight: "EU-USt-IdNr.",
            titleLine2: " gegen VIES",
            intro:
                "Prüfe, ob die USt-IdNr. eines Käufers in der offiziellen VIES-Datenbank der Europäischen Kommission gültig und aktiv ist. Unverzichtbar für B2B-Reverse-Charge-Rechnungen.",
            cardTitle: "VIES USt-IdNr.-Prüfung",
            country: "Land",
            vatNumber: "USt-IdNr.",
            validate: "Prüfen",
            validating: "Prüfung…",
            valid: "Gültige USt-IdNr.",
            invalid: "Keine gültige USt-IdNr.",
            errorEmpty: "Bitte eine USt-IdNr. eingeben.",
            errorService: "VIES nicht erreichbar. Bitte gleich nochmal versuchen.",
            reverseChargeTitle: "Reverse-Charge anwendbar",
            reverseChargeBody:
                "Bei grenzüberschreitenden B2B-Verkäufen an ein USt-registriertes Unternehmen in {country} berechnest du 0 % MwSt. auf deiner Rechnung. Der Käufer rechnet die MwSt. in seinem Land selbst ab.",
        },
        popular: {
            eyebrow: "Beliebte Routen",
            title: "Grenzüberschreitende MwSt., vorausgefüllt",
            intro: "Häufige EU-Verkaufswege. Klicke eine Route, um sie sofort in den Rechner zu laden.",
            standard: "Regelsatz",
            allCountries: "Alle Länder (ab Deutschland)",
        },
        share: {
            copied: "Link in die Zwischenablage kopiert",
            title: "EU-MwSt.-Rechner 2026 — VATcalc.eu",
            text: "Kostenloser EU-MwSt.-Rechner mit OSS-Schwellen-Tracker und Quartalsschätzer.",
        },
    },

    fr: {
        meta: { localeLabel: "Français", flag: "🇫🇷" },
        nav: {
            calculator: "Calculateur",
            vatRates: "Taux de TVA",
            howItWorks: "Comment ça marche",
            faq: "FAQ",
            share: "Partager l'outil",
            openCalculator: "Ouvrir le calculateur",
            language: "Langue",
        },
        banner: {
            needHelp: "Besoin d'aide pour la TVA ?",
            ctaText: "Essayez Quaderno — déclarations OSS, factures et conformité TVA automatisées.",
            cta: "Essayer Quaderno →",
            dismiss: "Fermer",
        },
        hero: {
            eyebrow: "Calculateur TVA UE · Taux 2026",
            titleLine1: "Calculez la ",
            titleHighlight: "TVA UE",
            titleLine2: " en quelques secondes.",
            subtitle:
                "Outil de TVA transfrontalière gratuit et instantané pour vendeurs e-commerce, freelances et équipes financières. Choisissez un pays vendeur, un pays acheteur et une catégorie produit — on s'occupe du reste.",
        },
        calc: {
            transactionDetails: "Détails de la transaction",
            summary: "Récapitulatif",
            netPrice: "Prix net (€)",
            sellerCountry: "Pays vendeur",
            buyerCountry: "Pays acheteur",
            productCategory: "Catégorie de produit",
            vatRateApplied: "Taux de TVA appliqué",
            vatAmount: "Montant de TVA",
            netPriceLabel: "Prix net",
            grossPrice: "Prix TTC (montant payé par le client)",
            vatIncluded: "TVA incluse",
            copyLink: "Copier le lien partageable",
            copyHint: "Copie un lien avec ces valeurs pré-remplies",
            copied: "Lien copié — partagez-le !",
            ossNotice:
                "Vous pouvez utiliser le régime OSS de l'UE — inscrivez-vous une fois en {seller} et déclarez la TVA pour toutes les ventes UE.",
            thresholdNotice:
                "Si vos ventes B2C transfrontalières annuelles restent sous 10 000 €, vous pouvez utiliser le taux de TVA de votre pays d'origine ({sellerFlag} {seller}).",
            domesticNotice:
                "Vente domestique en {sellerFlag} {seller} — règles locales de TVA, OSS non requis.",
        },
        rateLabels: {
            standard: "Normal",
            reduced: "Réduit",
            standardNoReduced: "Normal (pas de taux réduit)",
        },
        categories: {
            standard: { label: "Taux normal", description: "Par défaut — la majorité des biens et services" },
            reduced: { label: "Taux réduit", description: "Taux réduit propre au pays" },
            digital: { label: "Services numériques", description: "Apps, SaaS, téléchargements — taux normal" },
            food: { label: "Alimentation", description: "Épicerie — généralement réduit" },
            books: { label: "Livres & publications", description: "Imprimés + e-books — généralement réduit" },
            medicine: { label: "Médicaments", description: "Pharmacie — généralement réduit" },
        },
        oss: {
            eyebrow: "Outil 02 · Suivi du seuil",
            titleLine1: "Êtes-vous sous le seuil de ",
            titleHighlight: "10 000 €",
            titleLine2: " ?",
            intro:
                "Les vendeurs sous 10 000 € de ventes B2C transfrontalières par an peuvent appliquer le taux de TVA de leur pays. Au-delà, l'inscription OSS est obligatoire.",
            cardTitle: "Suivi du seuil OSS",
            homeCountry: "Pays d'origine",
            sales: "Ventes B2C transfrontalières (cette année)",
            thresholdUsed: "Seuil utilisé",
            ofCap: "du plafond annuel",
            below: "Sous le seuil — utilisez le taux d'origine",
            above: "Au-delà du seuil — OSS requis",
            belowDesc:
                "Vous pouvez continuer à appliquer la TVA {flag} {country} à tous les clients UE jusqu'à atteindre 10 000 € de ventes B2C transfrontalières cette année civile.",
            aboveDesc:
                "Vous avez dépassé 10 000 €. Inscrivez-vous à OSS en {country} et appliquez le taux de TVA du pays acheteur sur chaque vente B2C transfrontalière.",
        },
        estimator: {
            eyebrow: "Outil 03 · Estimation trimestrielle",
            titleLine1: "Que dois-je",
            titleLine2: " ce trimestre ?",
            intro:
                "Ajoutez jusqu'à 10 lignes de ventes par pays de destination. Nous appliquons le taux normal de chaque pays et totalisons votre obligation OSS.",
            salesRows: "Lignes de ventes",
            addRow: "Ajouter une ligne",
            max: "max",
            country: "Pays",
            sales: "Ventes (€)",
            vatRate: "Taux de TVA",
            vatOwed: "TVA due",
            totalSales: "Ventes totales",
            totalVat: "TVA totale due",
            fileViaOss: "Déclarer via OSS",
            ossSubtitle: "Une déclaration trimestrielle pour toutes les ventes UE",
            removeRow: "Supprimer la ligne",
            exportCsv: "Exporter CSV",
            exportPdf: "Exporter PDF",
            csvExported: "CSV téléchargé",
            pdfExported: "PDF téléchargé",
            pdfError: "Impossible de générer le PDF",
            pdfTitle: "Estimation TVA trimestrielle",
            pdfGenerated: "Généré",
        },
        how: {
            eyebrow: "Bases",
            title: "Comment fonctionne la TVA UE pour les vendeurs en ligne",
            intro:
                "Trois concepts couvrent ~90 % des décisions TVA transfrontalières pour les vendeurs digitaux et e-commerce en 2026.",
            items: [
                {
                    title: "Qu'est-ce que la TVA ?",
                    body: "La Taxe sur la Valeur Ajoutée est une taxe à la consommation prélevée sur les biens et services dans l'UE. Le vendeur la collecte auprès de l'acheteur et la reverse à l'administration fiscale du pays acheteur.",
                },
                {
                    title: "La règle du seuil 10 000 €",
                    body: "Si vos ventes B2C transfrontalières annuelles restent sous 10 000 €, vous pouvez continuer à appliquer la TVA de votre pays. Au-delà, vous devez appliquer le taux du pays acheteur.",
                },
                {
                    title: "Qu'est-ce qu'OSS ?",
                    body: "Le régime One-Stop Shop permet de s'inscrire une seule fois dans son pays d'origine et de déposer une seule déclaration trimestrielle pour toutes les ventes transfrontalières UE — fini les 27 inscriptions séparées.",
                },
            ],
        },
        rates: {
            eyebrow: "Référence",
            title: "Taux de TVA dans les 27 pays de l'UE",
            search: "Rechercher un pays…",
            country: "Pays",
            standard: "Normal",
            reduced1: "Réduit 1",
            reduced2: "Réduit 2",
            noMatch: 'Aucun pays ne correspond à "{q}".',
            lastUpdated:
                "Source : Commission européenne. Taux mis à jour : janvier 2026.",
        },
        faq: {
            eyebrow: "Questions fréquentes",
            title: "Les questions que les vendeurs nous posent",
            intro: "Réponses claires aux scénarios TVA transfrontaliers les plus courants.",
            items: [
                {
                    q: "Dois-je facturer la TVA si je vends de Lettonie vers l'Allemagne ?",
                    a: "Oui, si vous vendez en B2C et avez dépassé le seuil de 10 000 €, vous devez facturer la TVA allemande (19 % normal) et la reverser via OSS. Sous 10 000 €, vous pouvez continuer à appliquer le taux letton.",
                },
                {
                    q: "Qu'est-ce que le régime OSS ?",
                    a: "Le One-Stop Shop est un portail unique où les vendeurs UE déclarent et paient la TVA pour toutes leurs ventes B2C transfrontalières. Une inscription dans votre pays d'origine, une déclaration trimestrielle.",
                },
                {
                    q: "Que se passe-t-il si je dépasse le seuil de 10 000 € ?",
                    a: "Dès le dépassement, vous devez facturer chaque client au taux de son pays et déclarer via OSS. L'option du taux d'origine ne s'applique plus.",
                },
                {
                    q: "Les produits numériques sont-ils taxés différemment ?",
                    a: "Depuis 2015, les services numériques (apps, SaaS, e-books, streaming) sont toujours taxés dans le pays acheteur au taux normal local — indépendamment du seuil 10 000 € applicable aux biens.",
                },
                {
                    q: "Dois-je facturer la TVA en B2B ?",
                    a: "Généralement non — les ventes B2B intra-UE relèvent de l'autoliquidation si l'acheteur fournit un numéro de TVA valide. L'acheteur autoliquide la TVA dans son pays ; vous facturez sans TVA.",
                },
                {
                    q: "Qu'est-ce qu'IOSS pour les importations sous 150 € ?",
                    a: "L'Import One-Stop Shop permet aux vendeurs hors UE (ou marketplaces) de collecter la TVA à la caisse pour les biens importés à 150 € ou moins, simplifiant la douane et évitant les frais surprise pour les acheteurs.",
                },
            ],
        },
        affiliateCard: {
            recommended: "Recommandé",
            title: "Automatisez votre conformité TVA",
            intro:
                "Arrêtez de suivre les seuils dans des tableurs. Taxually, Quaderno et Avalara gèrent OSS, factures et règles TVA pays par pays automatiquement.",
            getStarted: "Commencer",
            partners: {
                taxually: { name: "Taxually", desc: "Déclarations OSS & TVA transfrontalière dans toute l'UE." },
                quaderno: { name: "Quaderno", desc: "Conformité fiscale + facturation pour SaaS & e-commerce." },
                avalara: { name: "Avalara", desc: "Automatisation et reporting TVA niveau entreprise." },
            },
        },
        newsletter: {
            eyebrow: "Restez informé",
            titleLine1: "Abonnez-vous aux ",
            titleHighlight: "alertes TVA UE",
            intro:
                "Recevez un email à chaque changement de taux de TVA ou de règles OSS d'un État membre. Sans spam, désabonnement à tout moment.",
            name: "Votre nom",
            email: "vous@entreprise.eu",
            submit: "S'abonner",
            subscribing: "Inscription…",
            subscribed: "Abonné ✓",
            disclaimer:
                "En vous abonnant, vous acceptez de recevoir des mises à jour produit occasionnelles. Nous ne partageons jamais votre email.",
            errorMissing: "Veuillez saisir votre nom et votre email",
            errorGeneric: "Une erreur est survenue. Veuillez réessayer.",
            success: "Abonné — nous vous écrirons à chaque changement de TVA UE.",
        },
        footer: {
            resources: "Ressources",
            company: "Entreprise",
            about: "À propos",
            privacy: "Confidentialité",
            contact: "Contact",
            tagline:
                "Calculateur de TVA UE gratuit avec suivi du seuil OSS et estimation trimestrielle. Conçu pour les vendeurs transfrontaliers en 2026.",
            lastUpdated: "Taux de TVA mis à jour :",
            updateMonth: "Janvier 2026",
            source: "Source : Commission européenne",
            disclaimer:
                "Cet outil est fourni à titre informatif uniquement. Consultez un fiscaliste pour des conseils officiels.",
        },
        vies: {
            eyebrow: "Outil 04 · Vérification n° de TVA",
            titleLine1: "Validez tout ",
            titleHighlight: "numéro de TVA UE",
            titleLine2: " via VIES",
            intro:
                "Vérifiez si le numéro de TVA d'un acheteur est valide et actif dans la base officielle VIES de la Commission européenne. Indispensable pour la facturation B2B en autoliquidation.",
            cardTitle: "Vérification VIES du numéro de TVA",
            country: "Pays",
            vatNumber: "N° de TVA",
            validate: "Valider",
            validating: "Vérification…",
            valid: "Numéro de TVA valide",
            invalid: "Numéro de TVA invalide",
            errorEmpty: "Saisissez un numéro de TVA à valider.",
            errorService: "VIES indisponible. Veuillez réessayer dans un instant.",
            reverseChargeTitle: "Autoliquidation applicable",
            reverseChargeBody:
                "Pour les ventes B2B transfrontalières à une entreprise enregistrée à la TVA en {country}, vous facturez 0 % de TVA. L'acheteur autoliquide la TVA dans son pays.",
        },
        popular: {
            eyebrow: "Routes populaires",
            title: "TVA transfrontalière, pré-remplie",
            intro: "Couloirs de vente UE courants. Cliquez sur une route pour la charger instantanément dans le calculateur.",
            standard: "taux normal",
            allCountries: "Tous les pays (depuis l'Allemagne)",
        },
        share: {
            copied: "Lien copié dans le presse-papiers",
            title: "Calculateur TVA UE 2026 — VATcalc.eu",
            text: "Calculateur TVA UE gratuit avec suivi du seuil OSS et estimation trimestrielle.",
        },
    },

    es: {
        meta: { localeLabel: "Español", flag: "🇪🇸" },
        nav: {
            calculator: "Calculadora",
            vatRates: "Tipos de IVA",
            howItWorks: "Cómo funciona",
            faq: "FAQ",
            share: "Compartir",
            openCalculator: "Abrir calculadora",
            language: "Idioma",
        },
        banner: {
            needHelp: "¿Necesitas ayuda con el IVA?",
            ctaText: "Prueba Quaderno — declaraciones OSS, facturación y cumplimiento de IVA automatizados.",
            cta: "Probar Quaderno →",
            dismiss: "Cerrar",
        },
        hero: {
            eyebrow: "Calculadora de IVA UE · Tipos 2026",
            titleLine1: "Calcula el ",
            titleHighlight: "IVA UE",
            titleLine2: " en segundos.",
            subtitle:
                "Herramienta gratuita e instantánea de IVA transfronterizo para vendedores e-commerce, freelancers y equipos financieros. Elige país vendedor, comprador y categoría — nosotros nos encargamos del resto.",
        },
        calc: {
            transactionDetails: "Detalles de la transacción",
            summary: "Resumen",
            netPrice: "Precio neto (€)",
            sellerCountry: "País del vendedor",
            buyerCountry: "País del comprador",
            productCategory: "Categoría del producto",
            vatRateApplied: "Tipo de IVA aplicado",
            vatAmount: "Importe del IVA",
            netPriceLabel: "Precio neto",
            grossPrice: "Precio bruto (paga el cliente)",
            vatIncluded: "IVA incluido",
            copyLink: "Copiar enlace compartible",
            copyHint: "Copia un enlace con estos valores prerrellenados",
            copied: "Enlace copiado — ¡a compartir!",
            ossNotice:
                "Puedes usar el régimen OSS de la UE — regístrate una vez en {seller} y declara el IVA de todas tus ventas en la UE.",
            thresholdNotice:
                "Si tus ventas B2C transfronterizas anuales se mantienen por debajo de 10.000 €, puedes usar el tipo de IVA de tu país ({sellerFlag} {seller}).",
            domesticNotice:
                "Venta nacional en {sellerFlag} {seller} — se aplica el IVA local, OSS no es necesario.",
        },
        rateLabels: {
            standard: "General",
            reduced: "Reducido",
            standardNoReduced: "General (sin reducido)",
        },
        categories: {
            standard: { label: "Tipo general", description: "Por defecto — la mayoría de bienes y servicios" },
            reduced: { label: "Tipo reducido", description: "Tipo reducido específico del país" },
            digital: { label: "Servicios digitales", description: "Apps, SaaS, descargas — tipo general" },
            food: { label: "Alimentación", description: "Comestibles — habitualmente reducido" },
            books: { label: "Libros y publicaciones", description: "Impresos + e-books — habitualmente reducido" },
            medicine: { label: "Medicamentos", description: "Farmacia — habitualmente reducido" },
        },
        oss: {
            eyebrow: "Herramienta 02 · Seguimiento del umbral",
            titleLine1: "¿Estás por debajo del umbral de ",
            titleHighlight: "10.000 €",
            titleLine2: "?",
            intro:
                "Vendedores con menos de 10.000 € en ventas B2C transfronterizas anuales pueden aplicar el tipo de IVA de su país. Por encima es obligatorio el registro OSS.",
            cardTitle: "Seguimiento del umbral OSS",
            homeCountry: "País de origen",
            sales: "Ventas B2C transfronterizas (este año)",
            thresholdUsed: "Umbral utilizado",
            ofCap: "del límite anual",
            below: "Por debajo del umbral — usa tu IVA local",
            above: "Por encima del umbral — registro OSS requerido",
            belowDesc:
                "Puedes seguir aplicando el IVA de {flag} {country} a todos los clientes UE hasta superar 10.000 € en ventas B2C transfronterizas este año natural.",
            aboveDesc:
                "Has superado los 10.000 €. Regístrate en OSS en {country} y aplica el tipo de IVA del país comprador en cada venta B2C transfronteriza.",
        },
        estimator: {
            eyebrow: "Herramienta 03 · Estimador trimestral",
            titleLine1: "¿Cuánto debo",
            titleLine2: " este trimestre?",
            intro:
                "Añade hasta 10 filas de ventas por país de destino. Aplicamos el tipo general de cada país y sumamos tu obligación OSS.",
            salesRows: "Filas de ventas",
            addRow: "Añadir fila",
            max: "máx",
            country: "País",
            sales: "Ventas (€)",
            vatRate: "Tipo de IVA",
            vatOwed: "IVA a pagar",
            totalSales: "Ventas totales",
            totalVat: "IVA total a pagar",
            fileViaOss: "Declarar vía OSS",
            ossSubtitle: "Una declaración trimestral para todas las ventas UE",
            removeRow: "Eliminar fila",
            exportCsv: "Exportar CSV",
            exportPdf: "Exportar PDF",
            csvExported: "CSV descargado",
            pdfExported: "PDF descargado",
            pdfError: "No se pudo generar el PDF",
            pdfTitle: "Estimación trimestral de IVA",
            pdfGenerated: "Generado",
        },
        how: {
            eyebrow: "Fundamentos",
            title: "Cómo funciona el IVA UE para vendedores online",
            intro:
                "Tres conceptos cubren ~90 % de las decisiones de IVA transfronterizo para vendedores digitales y e-commerce en 2026.",
            items: [
                {
                    title: "¿Qué es el IVA?",
                    body: "El Impuesto sobre el Valor Añadido es un impuesto al consumo aplicado a bienes y servicios en la UE. El vendedor lo cobra al comprador y lo entrega a la autoridad fiscal del país del comprador.",
                },
                {
                    title: "La regla del umbral de 10.000 €",
                    body: "Si tus ventas B2C transfronterizas anuales se mantienen por debajo de 10.000 €, puedes seguir aplicando el IVA de tu país. Por encima debes aplicar el tipo del país del comprador.",
                },
                {
                    title: "¿Qué es OSS?",
                    body: "El régimen One-Stop Shop te permite registrarte una sola vez en tu país y presentar una única declaración trimestral de IVA para todas tus ventas UE — sin 27 registros distintos.",
                },
            ],
        },
        rates: {
            eyebrow: "Referencia",
            title: "Tipos de IVA en los 27 países UE",
            search: "Buscar país…",
            country: "País",
            standard: "General",
            reduced1: "Reducido 1",
            reduced2: "Reducido 2",
            noMatch: 'Ningún país coincide con "{q}".',
            lastUpdated:
                "Fuente: Comisión Europea. Tipos de IVA actualizados: enero 2026.",
        },
        faq: {
            eyebrow: "Preguntas frecuentes",
            title: "Lo que nos preguntan los vendedores",
            intro: "Respuestas claras a los escenarios de IVA transfronterizo más comunes.",
            items: [
                {
                    q: "¿Tengo que cobrar IVA si vendo de Letonia a Alemania?",
                    a: "Sí, si vendes B2C y has superado el umbral de 10.000 €, debes cobrar IVA alemán (19 % general) y declararlo vía OSS. Por debajo de 10.000 € puedes seguir aplicando el tipo letón.",
                },
                {
                    q: "¿Qué es el régimen OSS de la UE?",
                    a: "El One-Stop Shop es un portal único donde los vendedores UE declaran y pagan el IVA de todas sus ventas B2C transfronterizas. Un registro en tu país, una declaración trimestral.",
                },
                {
                    q: "¿Qué pasa si supero el umbral de 10.000 €?",
                    a: "Desde que lo superas, debes cobrar a cada cliente el tipo de IVA de su país y declararlo vía OSS. La opción del tipo de origen ya no se aplica.",
                },
                {
                    q: "¿Los productos digitales tributan distinto?",
                    a: "Desde 2015, los servicios digitales (apps, SaaS, e-books, streaming) tributan siempre en el país del comprador a su tipo general — independientemente del umbral de 10.000 € para bienes.",
                },
                {
                    q: "¿Cobro IVA en ventas B2B?",
                    a: "En general no — las ventas B2B intra-UE están sujetas a inversión del sujeto pasivo si el comprador aporta NIF-IVA válido. El comprador autoliquida en su país; tú facturas sin IVA.",
                },
                {
                    q: "¿Qué es IOSS para importaciones bajo 150 €?",
                    a: "El Import One-Stop Shop permite a vendedores no-UE (o marketplaces) cobrar el IVA en el momento de la compra para bienes importados de hasta 150 €, simplificando aduanas y evitando sorpresas para los compradores.",
                },
            ],
        },
        affiliateCard: {
            recommended: "Recomendado",
            title: "Automatiza tu cumplimiento de IVA",
            intro:
                "Deja de seguir umbrales en hojas de cálculo. Taxually, Quaderno y Avalara gestionan OSS, facturación y reglas de IVA por país automáticamente.",
            getStarted: "Empezar",
            partners: {
                taxually: { name: "Taxually", desc: "Declaraciones OSS y de IVA transfronterizo en toda la UE." },
                quaderno: { name: "Quaderno", desc: "Cumplimiento fiscal + facturación para SaaS y e-commerce." },
                avalara: { name: "Avalara", desc: "Automatización y reporting de IVA de nivel empresarial." },
            },
        },
        newsletter: {
            eyebrow: "Mantente al día",
            titleLine1: "Suscríbete a las ",
            titleHighlight: "alertas de IVA UE",
            intro:
                "Recibe un email cada vez que un Estado miembro cambie sus tipos de IVA o reglas OSS. Sin spam, baja en cualquier momento.",
            name: "Tu nombre",
            email: "tu@empresa.eu",
            submit: "Suscribirme",
            subscribing: "Suscribiendo…",
            subscribed: "Suscrito ✓",
            disclaimer:
                "Al suscribirte aceptas recibir actualizaciones ocasionales del producto. Nunca compartimos tu email.",
            errorMissing: "Por favor, introduce tu nombre y email",
            errorGeneric: "Algo salió mal. Inténtalo de nuevo.",
            success: "Suscrito — te avisamos cuando cambien los tipos de IVA UE.",
        },
        footer: {
            resources: "Recursos",
            company: "Empresa",
            about: "Sobre nosotros",
            privacy: "Privacidad",
            contact: "Contacto",
            tagline:
                "Calculadora de IVA UE gratuita con seguimiento del umbral OSS y estimador trimestral. Para vendedores transfronterizos en 2026.",
            lastUpdated: "Tipos de IVA actualizados:",
            updateMonth: "Enero 2026",
            source: "Fuente: Comisión Europea",
            disclaimer:
                "Esta herramienta es solo informativa. Consulta a un asesor fiscal para asesoramiento oficial.",
        },
        vies: {
            eyebrow: "Herramienta 04 · Verificación NIF-IVA",
            titleLine1: "Valida cualquier ",
            titleHighlight: "NIF-IVA UE",
            titleLine2: " con VIES",
            intro:
                "Comprueba si el NIF-IVA de un comprador es válido y está activo en la base oficial VIES de la Comisión Europea. Imprescindible para facturas B2B con inversión del sujeto pasivo.",
            cardTitle: "Validador VIES de NIF-IVA",
            country: "País",
            vatNumber: "NIF-IVA",
            validate: "Validar",
            validating: "Comprobando…",
            valid: "NIF-IVA válido",
            invalid: "NIF-IVA no válido",
            errorEmpty: "Introduce un NIF-IVA para validar.",
            errorService: "No se puede contactar con VIES. Inténtalo de nuevo.",
            reverseChargeTitle: "Inversión del sujeto pasivo aplicable",
            reverseChargeBody:
                "En ventas B2B transfronterizas a una empresa registrada en {country}, facturas con 0 % de IVA. El comprador autoliquida el IVA en su país.",
        },
        popular: {
            eyebrow: "Rutas populares",
            title: "IVA transfronterizo, ya rellenado",
            intro: "Rutas de venta UE habituales. Haz clic en una para cargarla al instante en la calculadora.",
            standard: "tipo general",
            allCountries: "Todos los países (desde Alemania)",
        },
        share: {
            copied: "Enlace copiado al portapapeles",
            title: "Calculadora de IVA UE 2026 — VATcalc.eu",
            text: "Calculadora de IVA UE gratuita con seguimiento del umbral OSS y estimador trimestral.",
        },
    },

    it: {
        meta: { localeLabel: "Italiano", flag: "🇮🇹" },
        nav: {
            calculator: "Calcolatore",
            vatRates: "Aliquote IVA",
            howItWorks: "Come funziona",
            faq: "FAQ",
            share: "Condividi",
            openCalculator: "Apri calcolatore",
            language: "Lingua",
        },
        banner: {
            needHelp: "Serve aiuto con l'IVA?",
            ctaText: "Prova Quaderno — dichiarazioni OSS, fatturazione e compliance IVA automatizzate.",
            cta: "Prova Quaderno →",
            dismiss: "Chiudi",
        },
        hero: {
            eyebrow: "Calcolatore IVA UE · Aliquote 2026",
            titleLine1: "Calcola l'",
            titleHighlight: "IVA UE",
            titleLine2: " in pochi secondi.",
            subtitle:
                "Strumento IVA transfrontaliero gratuito e immediato per venditori e-commerce, freelance e team finance. Scegli paese venditore, acquirente e categoria prodotto — al resto pensiamo noi.",
        },
        calc: {
            transactionDetails: "Dettagli transazione",
            summary: "Riepilogo",
            netPrice: "Prezzo netto (€)",
            sellerCountry: "Paese venditore",
            buyerCountry: "Paese acquirente",
            productCategory: "Categoria prodotto",
            vatRateApplied: "Aliquota IVA applicata",
            vatAmount: "Importo IVA",
            netPriceLabel: "Prezzo netto",
            grossPrice: "Prezzo lordo (paga il cliente)",
            vatIncluded: "IVA inclusa",
            copyLink: "Copia link condivisibile",
            copyHint: "Copia un link con questi valori precompilati",
            copied: "Link copiato — buona condivisione!",
            ossNotice:
                "Puoi usare il regime OSS UE — registrati una volta in {seller} e dichiara l'IVA per tutte le vendite UE.",
            thresholdNotice:
                "Se le tue vendite B2C transfrontaliere annue restano sotto i 10.000 €, puoi usare l'aliquota IVA del tuo paese ({sellerFlag} {seller}).",
            domesticNotice:
                "Vendita nazionale in {sellerFlag} {seller} — si applicano le regole IVA locali, OSS non necessario.",
        },
        rateLabels: {
            standard: "Ordinaria",
            reduced: "Ridotta",
            standardNoReduced: "Ordinaria (nessuna ridotta)",
        },
        categories: {
            standard: { label: "Aliquota ordinaria", description: "Default — la maggior parte di beni e servizi" },
            reduced: { label: "Aliquota ridotta", description: "Aliquota ridotta specifica del paese" },
            digital: { label: "Servizi digitali", description: "App, SaaS, download — aliquota ordinaria" },
            food: { label: "Alimentari", description: "Generi alimentari — solitamente ridotta" },
            books: { label: "Libri & pubblicazioni", description: "Cartacei + e-book — solitamente ridotta" },
            medicine: { label: "Medicinali", description: "Farmaci — solitamente ridotta" },
        },
        oss: {
            eyebrow: "Strumento 02 · Tracker soglia",
            titleLine1: "Sei sotto la soglia di ",
            titleHighlight: "10.000 €",
            titleLine2: "?",
            intro:
                "I venditori sotto i 10.000 € di vendite B2C transfrontaliere annue possono applicare l'IVA del proprio paese. Sopra, è obbligatoria la registrazione OSS.",
            cardTitle: "Tracker soglia OSS",
            homeCountry: "Paese d'origine",
            sales: "Vendite B2C transfrontaliere (anno corrente)",
            thresholdUsed: "Soglia utilizzata",
            ofCap: "del tetto annuale",
            below: "Sotto soglia — usa l'IVA del tuo paese",
            above: "Sopra soglia — registrazione OSS richiesta",
            belowDesc:
                "Puoi continuare ad applicare l'IVA {flag} {country} a tutti i clienti UE finché non superi 10.000 € di vendite B2C transfrontaliere nell'anno solare.",
            aboveDesc:
                "Hai superato i 10.000 €. Registrati a OSS in {country} e applica l'aliquota IVA del paese acquirente su ogni vendita B2C transfrontaliera.",
        },
        estimator: {
            eyebrow: "Strumento 03 · Stima trimestrale",
            titleLine1: "Quanto devo",
            titleLine2: " questo trimestre?",
            intro:
                "Aggiungi fino a 10 righe di vendite per paese di destinazione. Applichiamo l'aliquota ordinaria di ogni paese e totaliziamo il tuo obbligo OSS.",
            salesRows: "Righe vendite",
            addRow: "Aggiungi riga",
            max: "max",
            country: "Paese",
            sales: "Vendite (€)",
            vatRate: "Aliquota IVA",
            vatOwed: "IVA dovuta",
            totalSales: "Vendite totali",
            totalVat: "IVA totale dovuta",
            fileViaOss: "Dichiara via OSS",
            ossSubtitle: "Una dichiarazione trimestrale per tutte le vendite UE",
            removeRow: "Rimuovi riga",
            exportCsv: "Esporta CSV",
            exportPdf: "Esporta PDF",
            csvExported: "CSV scaricato",
            pdfExported: "PDF scaricato",
            pdfError: "Impossibile generare il PDF",
            pdfTitle: "Stima IVA trimestrale",
            pdfGenerated: "Generato",
        },
        how: {
            eyebrow: "Fondamenti",
            title: "Come funziona l'IVA UE per i venditori online",
            intro:
                "Tre concetti coprono il ~90 % delle decisioni IVA transfrontaliere per venditori digitali ed e-commerce nel 2026.",
            items: [
                {
                    title: "Che cos'è l'IVA?",
                    body: "L'Imposta sul Valore Aggiunto è un'imposta sui consumi applicata a beni e servizi in UE. Il venditore la incassa dall'acquirente e la versa all'autorità fiscale del paese acquirente.",
                },
                {
                    title: "La regola della soglia 10.000 €",
                    body: "Se le tue vendite B2C transfrontaliere annue restano sotto i 10.000 €, puoi continuare ad applicare l'IVA del tuo paese. Sopra, devi applicare quella del paese acquirente.",
                },
                {
                    title: "Cos'è OSS?",
                    body: "Il regime One-Stop Shop ti permette di registrarti una volta nel tuo paese e presentare una sola dichiarazione IVA trimestrale per tutte le vendite UE — niente 27 registrazioni separate.",
                },
            ],
        },
        rates: {
            eyebrow: "Riferimento",
            title: "Aliquote IVA nei 27 paesi UE",
            search: "Cerca un paese…",
            country: "Paese",
            standard: "Ordinaria",
            reduced1: "Ridotta 1",
            reduced2: "Ridotta 2",
            noMatch: 'Nessun paese corrisponde a "{q}".',
            lastUpdated:
                "Fonte: Commissione Europea. Aliquote aggiornate: gennaio 2026.",
        },
        faq: {
            eyebrow: "Domande frequenti",
            title: "Le domande dei venditori",
            intro: "Risposte chiare agli scenari IVA transfrontalieri più comuni.",
            items: [
                {
                    q: "Devo addebitare l'IVA se vendo dalla Lettonia alla Germania?",
                    a: "Sì, se vendi B2C e hai superato la soglia di 10.000 €, devi addebitare l'IVA tedesca (19 % ordinaria) e versarla via OSS. Sotto i 10.000 € puoi continuare ad applicare l'aliquota lettone.",
                },
                {
                    q: "Cos'è il regime OSS UE?",
                    a: "One-Stop Shop è un portale unico dove i venditori UE dichiarano e pagano l'IVA per tutte le vendite B2C transfrontaliere. Una registrazione nel tuo paese, una dichiarazione trimestrale.",
                },
                {
                    q: "Cosa succede se supero la soglia di 10.000 €?",
                    a: "Dal momento del superamento, devi addebitare a ciascun cliente l'aliquota del suo paese e dichiararla via OSS. L'opzione dell'aliquota d'origine non è più applicabile.",
                },
                {
                    q: "I prodotti digitali sono tassati diversamente?",
                    a: "Dal 2015, i servizi digitali (app, SaaS, e-book, streaming) sono sempre tassati nel paese acquirente all'aliquota ordinaria locale — indipendentemente dalla soglia 10.000 € per i beni.",
                },
                {
                    q: "Devo addebitare l'IVA su vendite B2B?",
                    a: "In genere no — le vendite B2B intra-UE sono soggette a reverse charge se l'acquirente fornisce una P.IVA valida. L'acquirente autoliquida nel suo paese; tu fatturi senza IVA.",
                },
                {
                    q: "Cos'è IOSS per importazioni sotto 150 €?",
                    a: "Import One-Stop Shop permette ai venditori extra-UE (o marketplace) di incassare l'IVA al checkout per beni importati fino a 150 €, semplificando la dogana ed evitando sorprese ai clienti.",
                },
            ],
        },
        affiliateCard: {
            recommended: "Consigliato",
            title: "Automatizza la tua compliance IVA",
            intro:
                "Smetti di tracciare le soglie nei fogli di calcolo. Taxually, Quaderno e Avalara gestiscono OSS, fatturazione e regole IVA per paese in automatico.",
            getStarted: "Inizia",
            partners: {
                taxually: { name: "Taxually", desc: "Dichiarazioni OSS e IVA transfrontaliere in tutta l'UE." },
                quaderno: { name: "Quaderno", desc: "Compliance fiscale + fatturazione per SaaS ed e-commerce." },
                avalara: { name: "Avalara", desc: "Automazione e reporting IVA di livello enterprise." },
            },
        },
        newsletter: {
            eyebrow: "Resta aggiornato",
            titleLine1: "Iscriviti agli ",
            titleHighlight: "alert IVA UE",
            intro:
                "Ricevi un'email ogni volta che uno Stato membro cambia aliquote IVA o regole OSS. Niente spam, disiscriviti quando vuoi.",
            name: "Il tuo nome",
            email: "tu@azienda.eu",
            submit: "Iscriviti",
            subscribing: "Iscrizione…",
            subscribed: "Iscritto ✓",
            disclaimer:
                "Iscrivendoti accetti di ricevere occasionali aggiornamenti di prodotto. Non condividiamo mai la tua email.",
            errorMissing: "Inserisci nome ed email",
            errorGeneric: "Qualcosa è andato storto. Riprova.",
            success: "Iscritto — ti scriveremo a ogni cambio IVA UE.",
        },
        footer: {
            resources: "Risorse",
            company: "Azienda",
            about: "Chi siamo",
            privacy: "Privacy",
            contact: "Contatti",
            tagline:
                "Calcolatore IVA UE gratuito con tracker soglia OSS e stima trimestrale. Per venditori transfrontalieri nel 2026.",
            lastUpdated: "Aliquote IVA aggiornate:",
            updateMonth: "Gennaio 2026",
            source: "Fonte: Commissione Europea",
            disclaimer:
                "Questo strumento è solo a scopo informativo. Consulta un commercialista per consulenze ufficiali.",
        },
        vies: {
            eyebrow: "Strumento 04 · Verifica P.IVA",
            titleLine1: "Verifica qualsiasi ",
            titleHighlight: "P.IVA UE",
            titleLine2: " su VIES",
            intro:
                "Controlla se la P.IVA di un acquirente è valida e attiva nella banca dati ufficiale VIES della Commissione Europea. Indispensabile per la fatturazione B2B in reverse charge.",
            cardTitle: "Verificatore VIES P.IVA",
            country: "Paese",
            vatNumber: "P.IVA",
            validate: "Verifica",
            validating: "Verifica…",
            valid: "P.IVA valida",
            invalid: "P.IVA non valida",
            errorEmpty: "Inserisci una P.IVA da verificare.",
            errorService: "VIES non raggiungibile. Riprova tra un attimo.",
            reverseChargeTitle: "Si applica il reverse charge",
            reverseChargeBody:
                "Per vendite B2B transfrontaliere a un'azienda con P.IVA in {country}, fatturi con 0 % di IVA. L'acquirente autoliquida l'IVA nel suo paese.",
        },
        popular: {
            eyebrow: "Rotte popolari",
            title: "IVA transfrontaliera, già precompilata",
            intro: "Rotte di vendita UE comuni. Clicca una rotta per caricarla istantaneamente nel calcolatore.",
            standard: "aliquota ordinaria",
            allCountries: "Tutti i paesi (dalla Germania)",
        },
        share: {
            copied: "Link copiato negli appunti",
            title: "Calcolatore IVA UE 2026 — VATcalc.eu",
            text: "Calcolatore IVA UE gratuito con tracker soglia OSS e stima trimestrale.",
        },
    },
};

export const SUPPORTED_LOCALES = ["en", "de", "fr", "es", "it"];
export const DEFAULT_LOCALE = "en";

export default translations;
